import { useEffect } from 'react';
import { createRoot, type Root } from 'react-dom/client';

import { ReactBridgePageWrapper } from '@/components/ReactBridgePageWrapper';
import { DialogHost } from '@/dialogs/DialogHost';
import {
    type OpenPublishToRecipientsParams,
    useDialogHost,
} from '@/hooks/useDialogHost';

/**
 * AngularJS controllers cannot call `useDialogHost`. This mounts one
 * DialogHost for the browse toolbar, the item-editor grouping tab, and the
 * publications re-run action.
 */
let root: Root | null = null;
let api: { openPublishToRecipients: (props: OpenPublishToRecipientsParams) => void } | null = null;
const pending: OpenPublishToRecipientsParams[] = [];

const DialogHostBinder = () => {
    const dialogHost = useDialogHost();

    useEffect(() => {
        api = dialogHost;
        const queued = pending.splice(0, pending.length);
        queued.forEach((props) => dialogHost.openPublishToRecipients(props));
        return () => {
            if (api === dialogHost) {
                api = null;
            }
        };
    }, [dialogHost]);

    return null;
};

DialogHostBinder.displayName = 'DialogHostBinder';

const ensureMounted = () => {
    if (root) {
        return;
    }
    const node = document.createElement('div');
    node.id = 'react-dialog-host';
    node.hidden = true;
    document.body.appendChild(node);
    root = createRoot(node);
    root.render(
        <ReactBridgePageWrapper>
            <DialogHost>
                <DialogHostBinder />
            </DialogHost>
        </ReactBridgePageWrapper>,
    );
};

const openPublishToRecipients = (props: OpenPublishToRecipientsParams) => {
    if (api) {
        api.openPublishToRecipients(props);
        return;
    }
    pending.push(props);
    ensureMounted();
};

const resetDialogHostBridge = () => {
    pending.splice(0, pending.length);
    api = null;
    root?.unmount();
    root = null;
    document.getElementById('react-dialog-host')?.remove();
};

export { openPublishToRecipients, resetDialogHostBridge };
